import React from 'react';
import { Button } from '@/components/ui/button';
import { CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { FREELANCER_PLAN, ORGANIZATION_PLAN } from '@/config/tierPricing';
import { ErrorSummary } from '@/components/signup/FieldError';
import SignupField from '@/components/signup/SignupField';

function PlanSummary({ isFreelancer, tier }) {
  const name = isFreelancer ? FREELANCER_PLAN.name : (tier?.name || ORGANIZATION_PLAN.name);
  const price = isFreelancer
    ? `SGD $${FREELANCER_PLAN.priceSgd.toFixed(2)}`
    : `SGD $${ORGANIZATION_PLAN.onboardingSgd.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  return (
    <div className="bg-slate-50 p-4 rounded-lg" data-testid="details-plan-summary">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Selected Plan</p>
          <p className="text-xs text-muted-foreground">{name}</p>
        </div>
        <div className="text-right">
          <p className="font-bold text-lg text-primary">{price}</p>
          <p className="text-xs text-muted-foreground">{isFreelancer ? 'per month' : 'per year'}</p>
        </div>
      </div>
    </div>
  );
}

const ReferralApplied = ({ code }) => (code ? (
  <div className="bg-green-50 border border-green-200 p-3 rounded-lg" data-testid="referral-applied">
    <div className="flex items-center gap-2">
      <CheckCircle2 className="w-4 h-4 text-green-600" />
      <span className="text-sm text-green-700">Referral code applied: {code}</span>
    </div>
  </div>
) : null);

export default function DetailsStep({ accountType, tier, formData, errors = {}, onChange, onBlur, onBack, onNext }) {
  const isFreelancer = accountType === 'freelancer';
  const common = { onChange, onBlur };

  return (
    <>
      <CardHeader>
        <Button variant="ghost" size="sm" onClick={onBack} className="w-fit -ml-2 mb-2" data-testid="details-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <CardTitle className="text-xl">{isFreelancer ? 'Your Details' : 'Organization Details'}</CardTitle>
        <CardDescription>
          {isFreelancer ? 'Just the basics — takes under a minute' : 'Just the basics — industry and sites are set up after you sign in'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <PlanSummary isFreelancer={isFreelancer} tier={tier} />
        <ErrorSummary errors={errors} testId="details-error-summary" />

        {!isFreelancer && (
          <SignupField id="company_name" label="Company Name *" value={formData.company_name} {...common}
            placeholder="Enter your company name" error={errors.company_name} testId="company-name-input" />
        )}
        <SignupField id="full_name" label="Your Full Name *" value={formData.full_name} {...common}
          placeholder="Enter your full name" error={errors.full_name} testId="full-name-input" />
        <SignupField id="contact_number" label="Contact Number (optional)" value={formData.contact_number}
          onChange={onChange} placeholder="+65 9123 4567" testId="contact-input" />

        <ReferralApplied code={formData.referral_code} />

        <Button onClick={onNext} className="w-full" data-testid="details-next-btn">
          Continue <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardContent>
    </>
  );
}
